import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type ProductData } from "~/domains/product/types/ProductData";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { useRef, useState } from "react";
import { Product } from "~/domains/product/Product";
import { IconButton, Box, styled } from "~/common/mui";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import { useDebouncedState } from "~/common/hooks/useDebouncedValue";
import DeleteIcon from "@mui/icons-material/Delete";

const PRODUCT_SIZE_S = "196px";
const PRODUCT_SIZE_M = "228px";
const PRODUCT_SIZE_L = "272px";
const QTY_SIZE_S = "180px";
const QTY_SIZE_M = "212px";
const QTY_SIZE_L = "256px";

const ItemRoot = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 4,
  display: "grid",
  rowGap: theme.spacing(2),
  gridTemplate: `"desc actions" auto "qty qty" auto "cmt cmt" auto / auto minmax(0, 1fr)`,
  "& .quantity": { gridArea: "qty", marginBottom: 0 },
  "& .comment": { gridArea: "cmt" },
  [theme.breakpoints.down("desktop-s")]: {
    padding: theme.spacing(2),
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      pointerEvents: "none",
    },
  },
  [theme.breakpoints.up("tablet")]: {
    "--product-size": "208px",
    columnGap: "var(--gutter)",
    gridTemplate: `"desc qty actions" auto "desc cmt cmt" auto / var(--product-size) 224px minmax(0, 1fr)`,
  },
  [theme.breakpoints.up("desktop-s")]: {
    "--product-size": PRODUCT_SIZE_S,
    gridTemplate: `"desc sub" auto / var(--product-size) minmax(0, 1fr)`,
    padding: `${theme.spacing(4)} 0`,
    "&::before": {
      content: "none",
    },
    "&:not(:last-child)::before": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      pointerEvents: "none",
    },
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--product-size": PRODUCT_SIZE_M,
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--product-size": PRODUCT_SIZE_L,
  },
}));

const Description = styled("div")(({ theme }) => ({
  gridArea: "desc",
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(1),
  "& > :first-child": {
    marginBottom: theme.spacing(1),
  },
}));

const SubItem = styled("div")(({ theme }) => ({
  display: "contents",
  position: "relative",
  "--qty-size": QTY_SIZE_S,
  [theme.breakpoints.up("desktop-s")]: {
    display: "grid",
    gridTemplate: `"qty cmt actions" auto / var(--qty-size) minmax(0, 1fr) auto`,
    padding: theme.spacing(2),
    alignSelf: "flex-start",
    gap: "var(--gutter)",
    "& .quantity": { order: 1 },
    "& .comment": { order: 2 },
    "& .actions": { order: 3 },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      pointerEvents: "none",
    },
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--qty-size": QTY_SIZE_M,
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--qty-size": QTY_SIZE_L,
  },
}));

const Actions = styled("div")(({ theme }) => ({
  gridArea: "actions",
  display: "flex",
  flexFlow: "column",
  alignSelf: "flex-start",
  justifySelf: "flex-end",
  gap: theme.spacing(5),
  [theme.breakpoints.up("tablet")]: {
    flexFlow: "row-reverse",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(2),
  },
  '&[aria-disabled="true"] .icon': {
    opacity: theme.palette.action.disabledOpacity,
    pointerEvents: "none",
  },
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(-1.5),
}));

const IconStyled = styled(DeleteIcon)(({ theme }) => ({
  "--icon-size": "24px",
  fontSize: "var(--icon-size)",
  [theme.breakpoints.up("desktop-s")]: {
    "--icon-size": "16px",
  },
}));

const DesignationBox = styled(Box)(({ theme }) => ({
  ...theme.typography.titleMedium,
  display: "flex",
  fontWeight: "bold",
}));

interface LineItemProps {
  data: LineItem;
  product: ProductData;
  onDelete: () => void;
  onChange: (changes: Partial<LineItem>) => void;
  actionsDisabled?: boolean;
}

export const B2CProductListItem = ({ data, product, onChange, onDelete, actionsDisabled = false }: LineItemProps) => {
  const { t } = useTranslation();
  const actionsDisabledRef = useRef(actionsDisabled);
  actionsDisabledRef.current = actionsDisabled;
  const runChange = (changes: Partial<LineItem>) => {
    if (!actionsDisabledRef.current) {
      onChange(changes);
    }
  };
  const [quantity, setQuantity] = useDebouncedState({
    value: data.quantity,
    onChange: (quantity) => runChange({ quantity }),
    delay: 500,
  });
  const [comment, setComment] = useDebouncedState({
    value: data.comment,
    onChange: (comment) => runChange({ comment }),
    delay: 500,
  });
  const { toggleCommentOpen, isCommentOpen, canSeeComment } = useLineItemCommentLogic({
    comment: comment,
    product,
  });
  const packSize = data.packSize ?? 1;
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentOpen);
  return (
    <Product.Context value={product}>
      <ItemRoot>
        <Description>
          <Product.PrimaryImageWithLogos />
          <DesignationBox>
            <Product.PreferredIndicator className={"icon-margin"} />
            <Product.Designation />
          </DesignationBox>
          <Product.MMDTag />
          <Product.ShortDescription clickable={false} />
        </Description>
        <SubItem>
          <Actions className="actions" aria-disabled={actionsDisabled}>
            <IconButtonStyled
              color={"primary"}
              disabled={actionsDisabled}
              onClick={() => {
                if (!actionsDisabledRef.current) {
                  onDelete();
                }
              }}
            >
              <IconStyled />
            </IconButtonStyled>
            {canSeeComment && (
              <CommentButton
                onClick={() => {
                  if (!actionsDisabledRef.current) {
                    toggleComment();
                  }
                }}
                open={isCommentOpen}
                fontSize={"small"}
                className="icon"
              />
            )}
          </Actions>
          <QuantityInput
            className="quantity"
            onValueChange={setQuantity}
            value={quantity}
            step={packSize}
            min={packSize}
            disabled={actionsDisabled}
          />
          <Box className="comment" hidden={!isCommentOpen}>
            <CommentInput
              value={data.comment ?? ""}
              onChange={(comment) => runChange({ comment })}
              placeholder={t("page.cart.b2c.item.comment.placeholder")}
              disabled={actionsDisabled}
              ref={commentRef}
            />
          </Box>
        </SubItem>
      </ItemRoot>
    </Product.Context>
  );
};
const useLineItemCommentLogic = ({
  comment,
  product,
  initiallyOpen = true,
}: Pick<LineItem, "comment"> & {
  comment?: string;
  initiallyOpen?: boolean;
  product: ProductData;
}) => {
  const canSeeComment = canUseCommentsForProduct(product.marketType);
  const [isCommentOpen, setIsCommentOpen] = useState(() => canSeeComment && initiallyOpen);
  const toggleCommentOpen = () => setIsCommentOpen((prev) => !prev);
  return { toggleCommentOpen, isCommentOpen, canSeeComment };
};
